import type { CampaignMergeExecuteResponse, MergeResolutionEntry } from '../services/api/generated/model';
import { Campaign } from './campaign.interface';

export interface ParameterConflict {
  name: string;
  unit: string;
  valueA: string | number | null;
  valueB: string | number | null;
  /** 'value' for the row's own value (redundant with measureName); anything else names a distinct sub-variable. */
  parameterKey: string;
}

export type ChosenSource = 'a' | 'b' | 'concat';

export const CONCAT_ENTITY_TYPES = new Set(['operator', 'comment', 'timestamp']);

/** General-row entity types where concatenation is meaningless (scalar fields). */
export const SCALAR_GENERAL_ENTITY_TYPES = new Set(['timestamp']);

export interface MergeConflict {
  entityType: 'measure' | 'input' | 'input_data' | 'info_text' | 'info_choice' | 'operator' | 'comment' | 'timestamp';
  /** Numeric row id for merges, opaque string handle for manual imports; the dialog echoes it verbatim. */
  entityKey: string | number;
  measureName: string;
  localisationName: string;
  parameters: ParameterConflict[];
}

export interface MergeAnalysis {
  hasConflicts: boolean;
  conflicts: MergeConflict[];
  campaignAId: number;
  campaignBId: number;
}

export interface ConflictResolution {
  entityType: string;
  entityKey: string | number;
  chosenSource: ChosenSource;
}

/** Merge keys are numeric on the wire; the shared dialog carries them opaquely so it can also serve manual imports. */
export const toMergeResolutionEntry = (resolution: ConflictResolution): MergeResolutionEntry => ({
  entityType: resolution.entityType,
  entityKey: Number(resolution.entityKey),
  chosenSource: resolution.chosenSource,
});

export interface MergeExecutePayload {
  campaignAId: number;
  campaignBId: number;
  resolutions?: ConflictResolution[];
}

export type MergeExecuteResponse = Omit<CampaignMergeExecuteResponse, 'campaign'> & { campaign: Campaign };

export interface ImportResolveResponse {
  campaign: Campaign;
  resolved?: boolean;
  imported?: number;
  updated?: number;
  auscultations?: number;
  outOfBounds?: number;
}
